import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'

import { Form, FormField } from '@/Core/UI/Form'
import { CostCurrencyField } from '@/Ship/Components/CostCurrencyField'
import { FieldCell, FieldGrid, FileFieldCell } from '@/Ship/Components/EntityCard'
import { renderUsersView } from '@/Ship/Helpers/fieldViews'
import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'
import { BaseViewFieldTemplate } from '@/Ship/Templates/Form/BaseViewFieldTemplate'

import { TRANSPORT_OFFER_CHOOSE_RESPONSIBLE } from '../Contracts/ITransportOffer'
import { OFFER_FIELD_LABELS as L } from '../Contracts/OfferFieldLabels'
import { ApplyOfferCreateDefaultsTask } from '../Tasks/ApplyOfferCreateDefaultsTask'

import type { OfferFormContextLike } from '../Tasks/ApplyOfferCreateDefaultsTask'
import type { FC, ReactNode } from 'react'

/** Ship-шаблон — .jsx без типів; children у пропсах відновлюємо аліасом. */
const ViewFieldTemplate = BaseViewFieldTemplate as FC<{ field: { label: string }; children?: ReactNode }>

interface Props {
    crudContext: { resource?: { key?: string; fields?: Record<string, unknown> } } | undefined
    formContext: OfferFormContextLike | undefined
}

/** Значення за замовчуванням — один раз на відкриту форму створення. */
const OfferCreateDefaults = ({ formContext }: { formContext: OfferFormContextLike }) => {
    useEffect(() => {
        void ApplyOfferCreateDefaultsTask(formContext)
    }, [formContext])

    return null
}

/**
 * Автор для «лише логіста» (ПР-02): бек прийме лише його самого, тож
 * вибору немає — у формі видно, хто стоїть автором, без select-а.
 */
const ResponsibleReadOnly = observer(({ formContext }: { formContext: OfferFormContextLike }) => (
    <ViewFieldTemplate field={{ label: L.responsible }}>
        {renderUsersView(formContext.values?.['responsible'])}
    </ViewFieldTemplate>
))

/**
 * Картка цінової пропозиції (`SPEC-024` §8.2; легасі
 * `{Request,Tender,Application}Container/Templates/OfferFormTemplate.jsx`) —
 * одна на три власники, склад полів — з ресурсу:
 *
 *  - **«Перевізник»** — першим, лише в пропозиції заявки;
 *  - **«Запропонована ціна»** з валютою одним полем, EUR за замовчуванням;
 *  - **«Коментар»**, **«Автор пропозиції»** (поточний за замовчуванням);
 *  - **«Файли»** — лише в запиту й тендера, двокроково й при створенні;
 *  - **дати створення й оновлення** — останніми, лише в збереженої.
 *
 * Хто може обирати автора, вирішує бек (колекційне `chooseResponsible`); ролей
 * шаблон не питає.
 */
export const OfferFormTemplate = observer(({ crudContext, formContext }: Props) => {
    const fields = crudContext?.resource?.fields ?? {}
    const resourceKey = crudContext?.resource?.key ?? ''
    const uuid = formContext?.values?.['uuid']
    const isNew = typeof uuid !== 'string' || uuid === ''

    if (!formContext) return null

    const canChooseResponsible = CheckGlobalResourceAccessTask(resourceKey, TRANSPORT_OFFER_CHOOSE_RESPONSIBLE)
    const showResponsibleReadOnly = formContext.isEditable === true && !canChooseResponsible

    return (
        <Form formContext={formContext}>
            {isNew ? <OfferCreateDefaults formContext={formContext} /> : null}
            <FieldGrid>
                {'carrier' in fields ? (
                    <FieldCell wide>
                        <FormField name="carrier" />
                    </FieldCell>
                ) : null}
                <FieldCell wide>
                    <CostCurrencyField
                        formContext={formContext}
                        amount="offeredPrice"
                        currency="offeredPriceCurrency"
                        label={L.offeredPrice}
                        isRequired
                    />
                </FieldCell>
                <FieldCell wide>
                    <FormField name="comment" />
                </FieldCell>
                <FieldCell wide>
                    {showResponsibleReadOnly ? <ResponsibleReadOnly formContext={formContext} /> : <FormField name="responsible" />}
                </FieldCell>
                {'files' in fields ? (
                    <FieldCell wide>
                        <FileFieldCell formContext={formContext} name="files" label={L.files} />
                    </FieldCell>
                ) : null}
                {isNew ? null : (
                    <>
                        <FieldCell>
                            <FormField name="createdAt" />
                        </FieldCell>
                        <FieldCell>
                            <FormField name="updatedAt" />
                        </FieldCell>
                    </>
                )}
            </FieldGrid>
        </Form>
    )
})
